// First-touch acquisition capture.
//
// Written 2026-08-27 after a qualified Fit Call application (Taylor Eggleston)
// arrived with `referrer: "https://www.google.com/"` and nothing else. We knew
// she came from Google and could not tell which page won her, how she moved
// through the site, or whether it was paid or organic. Fathom cannot answer it
// either — it is cookieless and anonymous by design, so there is no per-visitor
// journey to look up.
//
// So the landing context is captured HERE, on the first page of the session,
// and carried to whatever form the visitor eventually submits.
//
// What this still cannot tell you: the search keyword. Google has stripped the
// query from the referrer header since 2011, and no amount of instrumentation
// brings it back. Google Search Console is the only source for that.

const KEY = "fc:first-touch"

const CLICK_ID_PARAMS = [
  "gclid", // Google Ads
  "gbraid", // Google Ads, iOS app-to-web
  "wbraid", // Google Ads, web-to-app
  "fbclid", // Meta
  "msclkid", // Microsoft Ads
  "li_fat_id", // LinkedIn
  "ttclid", // TikTok
  "twclid", // X
] as const

export type FirstTouch = {
  landing_path: string
  landing_query: string
  referrer: string
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_term: string
  utm_content: string
  click_ids: Record<string, string>
}

const EMPTY: FirstTouch = {
  landing_path: "",
  landing_query: "",
  referrer: "",
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_term: "",
  utm_content: "",
  click_ids: {},
}

// sessionStorage throws outright in some privacy modes, so every access is
// guarded. Losing attribution must never break a form.
function read(): FirstTouch | null {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : null
  } catch {
    return null
  }
}

/**
 * Record where this session started. Called once from the client entrypoint on
 * every page load; the first call of a session wins, so a visitor who lands on
 * a blog post and later reaches /apply is still credited to the post.
 *
 * A same-origin referrer means this is not really a first touch (a hard reload
 * mid-session, or a link from another page of ours), so the stored value is
 * left alone.
 */
export function captureFirstTouch(): void {
  if (typeof window === "undefined") return
  if (read()) return

  try {
    const params = new URLSearchParams(window.location.search)
    const referrer = document.referrer || ""
    const sameOrigin = referrer.startsWith(`${window.location.origin}/`)

    const click_ids: Record<string, string> = {}
    for (const name of CLICK_ID_PARAMS) {
      const value = params.get(name)
      if (value) click_ids[name] = value
    }

    const touch: FirstTouch = {
      landing_path: window.location.pathname,
      landing_query: window.location.search.replace(/^\?/, ""),
      referrer: sameOrigin ? "" : referrer,
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_term: params.get("utm_term") || "",
      utm_content: params.get("utm_content") || "",
      click_ids,
    }

    window.sessionStorage.setItem(KEY, JSON.stringify(touch))
  } catch {
    // Attribution is best-effort. Never let it throw into the app.
  }
}

/**
 * The stored first touch, or empty values when nothing was captured (SSR,
 * blocked storage, or a session that started before this shipped).
 */
export function firstTouch(): FirstTouch {
  if (typeof window === "undefined") return EMPTY
  return read() ?? EMPTY
}
